/**
 * CESKY ZAPIS CISLA V FORMULARI — JEDEN PARSER PRO CELY ADMIN (krok EDIT2 bod 1).
 *
 * Zivy pruchod EDIT1 (13. 10. 2026, nalez B1): pole „Měsíční nájemné Kč" bylo
 * nativni `<input type="number">`. Prohlizec do nej nepusti ani mezeru, ani
 * desetinnou carku, takze z prirozeneho ceskeho zapisu `15 000,50` zustalo
 * v poli `1500050` — a tato hodnota se BEZ JAKEHOKOLI VAROVANI ulozila jako
 * smluvni castka. Nativni `type="number"` proto u penez a ploch nestaci: pole
 * musi byt TEXTOVE (`inputMode="decimal"`), vstup se musi prelozit tady a co
 * se prelozit neda, se nesmi ulozit.
 *
 * Kanonicky tvar je `-?\d+(\.\d+)?` — tentyz desetinny retezec, jaky cte
 * `parseDecimal` v `@reos/money` a jaky uklada API. Formatovani zpet do
 * ceskeho tvaru je tu taky, aby se pole po ulozeni ukazalo tak, jak ho clovek
 * napsal (`15 000,50`), ne jako `15000.5`.
 *
 * Opacny smer (kanonicka hodnota → VETA SMLOUVY) zije v enginu dokumentu
 * (`czechNumberText` v `@reos/documents`), protoze text smlouvy sklada server
 * a formular k nemu nema pristup. Jsou to dva prevody, ne dve kopie teze
 * pravdy: tenhle cte, co clovek napsal, ten druhy pise, co klient cte.
 */

/** Oddelovace tisicu, ktere clovek do pole dostane (vc. nezlomitelnych). */
const GROUP_SEPARATORS = /[\s\u00a0\u202f\u2009\u2007']/g;

const CANONICAL = /^-?\d+(\.\d+)?$/;

export type CzechNumberParse =
  /** `value` je kanonicky desetinny retezec; `''` = prazdne pole. */
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false };

/**
 * Cesky zapis cisla → kanonicky desetinny retezec.
 *
 * `15 000,50` → `15000.5`, `15 000` → `15000`, `15000.5` → `15000.5`,
 * `1.500,50` → `1500.5` (kde je carka, je tecka oddelovac tisicu),
 * `abc` → chyba, prazdne → `{ ok: true, value: '' }`.
 */
export function parseCzechNumberInput(raw: string): CzechNumberParse {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: '' };
  let body = trimmed.replace(GROUP_SEPARATORS, '');
  // Carka v cisle znamena desetiny — tecky jsou pak oddelovace tisicu
  // (`1.500,50`). Bez carky je desetinny oddelovac tecka (`15000.5`).
  if (body.includes(',')) {
    body = body.replace(/\./g, '').replace(',', '.');
  }
  if (!CANONICAL.test(body)) return { ok: false };
  return { ok: true, value: canonical(body) };
}

/** `15000.50` → `15000.5`, `15000.` uz sem neprijde (neprosel by regexem). */
function canonical(body: string): string {
  if (!body.includes('.')) return body;
  const trimmedFraction = body.replace(/0+$/, '').replace(/\.$/, '');
  return trimmedFraction === '' || trimmedFraction === '-' ? '0' : trimmedFraction;
}

/**
 * Kanonicky desetinny retezec → cesky zapis pro POLE FORMULARE.
 *
 * `15000.5` → `15 000,50` (desetiny se doplni na dve mista, jak je u penez
 * zvykem), `15000` → `15 000`. Co kanonicke cislo neni, projde beze zmeny —
 * stejne pravidlo jako `czechDateText` v enginu: formatovac nikdy nezahodi
 * hodnotu, kterou nezna.
 */
export function formatCzechNumber(value: string): string {
  if (value === '') return '';
  if (!CANONICAL.test(value)) return value;
  const negative = value.startsWith('-');
  const body = negative ? value.slice(1) : value;
  const dot = body.indexOf('.');
  const whole = dot === -1 ? body : body.slice(0, dot);
  const fraction = dot === -1 ? '' : body.slice(dot + 1).padEnd(2, '0');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  return `${negative ? '-' : ''}${grouped}${fraction === '' ? '' : `,${fraction}`}`;
}

/**
 * ROZEPSANE CISLO, ne chyba.
 *
 * Pole uklada 450 ms po poslednim znaku (`DraftTextInput`), takze pauza
 * uprostred zapisu `15 000,` by jinak vyvolala hlasku „nerozumim" presne ve
 * chvili, kdy clovek teprve pise desetiny. Dokud vstup konci oddelovacem,
 * ceka se dal.
 */
export function isIncompleteCzechNumber(raw: string): boolean {
  return /[.,]$/.test(raw.trim());
}

/** Typy poli slovniku, ktere jsou cislo — tedy jdou pres tenhle parser. */
export function isNumericFieldType(fieldType: string | undefined): boolean {
  return fieldType === 'cislo' || fieldType === 'castka-kc';
}

/**
 * KANONIZACE HODNOTY Z API (krok UXD2).
 *
 * API posila penize jako `numeric(18,4)` („1500.0000"), jenze `formatCzechNumber`
 * desetiny nezahazuje, takze by v poli stalo „1 500,0000". Kanonizace je ta
 * TATAZ, kterou dela `parseCzechNumberInput` na vstupu od cloveka — proto tu
 * neni druhe pravidlo, jen druhy vstupni bod. Co cislo neni, projde beze zmeny.
 */
export function canonicalCzechNumber(value: string): string {
  const parsed = parseCzechNumberInput(value);
  return parsed.ok ? parsed.value : value;
}

/** Tri stavy, ve kterych muze byt rozepsane pole. Viz `readCzechNumberInput`. */
export type CzechNumberRead =
  /** Konci oddelovacem (`15 000,`) — clovek jeste pise, nic se nehlasi. */
  | { readonly state: 'incomplete' }
  /** Parser to neprecetl — volajici NESMI ulozit a ma u pole ukazat chybu. */
  | { readonly state: 'invalid' }
  | { readonly state: 'ok'; readonly value: string };

/**
 * JEDNO PRAVIDLO PRO VSECHNA CISELNA POLE ADMINU (krok UXD2).
 *
 * „Rozepsane / nesrozumitelne / hotove" resila doted jen `NumericFieldInput`
 * v editoru smlouvy. Mimo editor (faktura, predpis najmu, vyska domu) byla
 * pole nativni `type="number"` nebo surovy text, ktery sel do API tak, jak ho
 * clovek napsal. Aby obe skorapky — `CzechNumberInput` (bezny formular) i
 * `NumericFieldInput` (pole s odlozenym ukladanim) — rozhodovaly STEJNE,
 * rozhoduje se tady, ne v komponente.
 */
export function readCzechNumberInput(raw: string): CzechNumberRead {
  if (isIncompleteCzechNumber(raw)) return { state: 'incomplete' };
  const parsed = parseCzechNumberInput(raw);
  return parsed.ok ? { state: 'ok', value: parsed.value } : { state: 'invalid' };
}
